/**
 * PrintOrderModal — "Prepare print order…" on a print shop under Team > Local Resources
 * (TeamSection.jsx). docs/PRINT_ORDERS.md.
 *
 * Three steps:
 *   1. Files   — upload files (pick or drop) and/or add the project's current drawing set (the
 *                latest posted sheets, merged into one PDF when sent). Each file carries its own
 *                size, color, copies and binding.
 *   2. Order   — who at the shop gets it, estimate first or proceed and bill, delivery, needed-by
 *                date, a note.
 *   3. Preview — the email exactly as it will read; Send emails it in the sender's name (replies to
 *                them, a copy to them) and records the order under the shop.
 * `initial` (an earlier order row) prefills everything for "Order again". `onRecorded` reloads the
 * record when an order was saved but its email failed (the window stays open showing why).
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { getContactNamesByIds, getContactEmailsByIds } from '../services/contacts.js'
import { getPrintOrderPrep, uploadPrintFile, previewPrintOrder, sendPrintOrder } from '../services/printOrders.js'
import {
  PRINT_SIZES, PRINT_COLORS, PRINT_BINDINGS, PRINT_APPROVALS, PRINT_DELIVERY,
} from '../../../api/_printOrderOptions.js'

const FONT = '"DM Sans",sans-serif'
const MONO = '"DM Mono",monospace'
const label = { fontFamily: FONT, fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
const input = { width: '100%', boxSizing: 'border-box', fontFamily: FONT, fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const select = { ...input, padding: '6px 8px', fontSize: 12 }
const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: FONT, fontSize: 13, fontWeight: 500, cursor: 'pointer' }
const primaryBtn = (off) => ({ ...btnBase, background: '#07272D', color: '#fff', opacity: off ? 0.6 : 1, cursor: off ? 'default' : 'pointer' })
const quietBtn = { ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }
const smallBtn = { ...quietBtn, padding: '6px 12px', fontSize: 12 }
const muted = { fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.45 }

const STEPS = ['Files', 'Order', 'Preview']
const DEFAULT_OPTS = { size: 'as_drawn', color: 'bw', copies: 1, binding: 'loose' }
let _uid = 0
const uid = () => 'f' + (++_uid) + '_' + Date.now()

function fmtBytes(n) {
  if (!n) return ''
  if (n < 1024 * 1024) return Math.max(1, Math.round(n / 1024)) + ' KB'
  return (n / 1024 / 1024).toFixed(1) + ' MB'
}

function ChoiceCards({ options, value, onPick }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map(o => {
        const on = value === o.v
        return (
          <button key={o.v} type="button" onClick={() => onPick(o.v)}
            style={{ flex: '1 1 160px', textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
              border: '0.5px solid ' + (on ? 'var(--aj-accent)' : 'var(--aj-border-mid)'),
              background: on ? 'var(--aj-bg-muted)' : 'transparent', boxShadow: on ? 'inset 0 0 0 1px var(--aj-accent)' : 'none' }}>
            <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{o.l}</div>
            {o.d && <div style={{ ...muted, marginTop: 2 }}>{o.d}</div>}
          </button>
        )
      })}
    </div>
  )
}

function Select({ list, value, onChange, width }) {
  return (
    <select style={{ ...select, width }} value={value} onChange={e => onChange(e.target.value)}>
      {list.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  )
}

export default function PrintOrderModal({ projectId, org, initial, onClose, onSent, onRecorded }) {
  const [step, setStep] = useState(0)
  const [prep, setPrep] = useState(null)
  const [items, setItems] = useState(() => (initial && initial.items ? initial.items.map(it => ({
    ...it, key: uid(),
    // an earlier drawing set goes again as "the current set", so a reorder prints what's posted now
    ...(it.source === 'drawing_set' ? { name: 'Current drawing set', url: null, bytes: null } : {}),
  })) : []))
  const [uploading, setUploading] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const [names, setNames] = useState({})
  const [emails, setEmails] = useState({})
  const [checked, setChecked] = useState({})
  const [useGeneral, setUseGeneral] = useState(false)
  const [approval, setApproval] = useState((initial && initial.approval) || 'estimate_first')
  const [deliveryMethod, setDeliveryMethod] = useState((initial && initial.delivery_method) || 'pickup')
  const [deliveryAddress, setDeliveryAddress] = useState((initial && initial.delivery_address) || '')
  const [needBy, setNeedBy] = useState('')
  const [notes, setNotes] = useState((initial && initial.notes) || '')
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef(null)

  const contactIds = (org && org.fields['Contacts']) || []
  const primaryId = org && org.fields['Primary Contact'] && org.fields['Primary Contact'][0]
  const generalEmail = (org && org.fields['General Email']) || ''
  const senderName = ctx.currentMember?.fields?.['Member Name'] || ''
  const senderEmail = ctx.currentMember?.fields?.['Member Email'] || ''

  useEffect(() => {
    if (!contactIds.length) { setUseGeneral(!!generalEmail); return }
    getContactNamesByIds(contactIds).then(setNames)
    getContactEmailsByIds(contactIds).then(setEmails)
    const before = initial && initial.recipients ? initial.recipients.map(r => r.contactAirtableId).filter(Boolean) : []
    const pre = before.length ? before : (primaryId ? [primaryId] : contactIds.slice(0, 1))
    setChecked(Object.fromEntries(pre.map(id => [id, true])))
    setUseGeneral(!!(initial && initial.general_email))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org && org.id])

  useEffect(() => {
    if (!projectId) return
    let alive = true
    getPrintOrderPrep(projectId).then(p => { if (alive) setPrep(p) }).catch(() => { if (alive) setPrep({ drawings: [], project: {} }) })
    return () => { alive = false }
  }, [projectId])

  if (!projectId || !org) return null
  const orgName = org.fields['Organization Name'] || ''
  const postedSheets = prep ? prep.drawings.filter(d => d.included).length : 0
  const hasSet = items.some(it => it.source === 'drawing_set')
  const selectedIds = Object.keys(checked).filter(id => checked[id] && emails[id])

  function setItem(key, patch) { setItems(list => list.map(it => it.key === key ? { ...it, ...patch } : it)) }
  function removeItem(key) { setItems(list => list.filter(it => it.key !== key)) }
  function sameForAll() {
    const f = items[0]; if (!f) return
    setItems(list => list.map(it => ({ ...it, size: f.size, color: f.color, copies: f.copies, binding: f.binding })))
  }

  async function addFiles(fileList) {
    const files = Array.from(fileList || [])
    if (!files.length) return
    setError(''); setUploading(n => n + files.length)
    for (const file of files) {
      const key = uid()
      setItems(list => [...list, { key, ...DEFAULT_OPTS, source: 'upload', name: file.name, bytes: file.size, url: null, uploading: true }])
      try {
        const up = await uploadPrintFile(projectId, file)
        setItem(key, { url: up.url, uploading: false })
      } catch (e) {
        setItems(list => list.filter(it => it.key !== key))
        setError((e && e.message) || 'Upload failed for ' + file.name)
      }
      setUploading(n => n - 1)
    }
  }

  function addDrawingSet() {
    setItems(list => [{ key: uid(), ...DEFAULT_OPTS, size: '24x36', binding: 'bound', source: 'drawing_set', name: 'Current drawing set', url: null }, ...list])
  }

  const payload = () => ({
    projectId, orgAirtableId: org.id, orgName,
    recipients: selectedIds.map(id => ({ contactAirtableId: id, name: names[id] || '', email: emails[id] })),
    generalEmail: useGeneral && generalEmail ? generalEmail : null,
    items: items.map(({ key, uploading: _u, ...it }) => it),
    approval, deliveryMethod, deliveryAddress: deliveryAddress.trim() || null, needBy: needBy || null,
    notes: notes.trim() || null, senderName, senderEmail,
  })

  function toOrder() {
    if (!items.length) { setError('Add at least one file to print.'); return }
    if (uploading) { setError('Wait for the uploads to finish.'); return }
    setError(''); setStep(1)
  }

  async function toPreview() {
    if (!selectedIds.length && !(useGeneral && generalEmail)) { setError('Pick at least one person at the shop.'); return }
    if ((deliveryMethod === 'address' || deliveryMethod === 'ship') && !deliveryAddress.trim()) { setError('Type the delivery address.'); return }
    setBusy(true); setError('')
    try { setPreview(await previewPrintOrder(payload())); setStep(2) }
    catch (e) { setError((e && e.message) || 'Could not build the preview.') }
    setBusy(false)
  }

  async function send() {
    setBusy(true); setError('')
    try { const order = await sendPrintOrder(payload()); onSent(order) }
    catch (e) {
      setError((e && e.message) || 'Could not send the order.')
      setBusy(false)
      if (e && e.order && onRecorded) onRecorded() // saved but the email failed: the record shows it under the shop
    }
  }

  const toList = [
    ...selectedIds.map(id => (names[id] || '…') + ' <' + emails[id] + '>'),
    ...(useGeneral && generalEmail ? [generalEmail] : []),
  ].join(', ')

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="PrintOrderModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 820, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>Print order — {orgName}</div>
            <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
              {STEPS.map((s, i) => (
                <span key={s} style={{ fontFamily: FONT, fontSize: 11, color: i === step ? 'var(--aj-text)' : 'var(--aj-text-muted)', fontWeight: i === step ? 600 : 400 }}>
                  <span style={{ fontFamily: MONO }}>{i + 1}</span> {s}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} disabled={busy} style={{ background: 'transparent', border: 'none', cursor: busy ? 'default' : 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0 }}>
          {step === 0 && (<>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files) }}
              style={{ border: '1px dashed ' + (dragOver ? 'var(--aj-accent)' : 'var(--aj-border-mid)'), borderRadius: 8, padding: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: dragOver ? 'var(--aj-bg-muted)' : 'transparent' }}>
              <button style={smallBtn} onClick={() => fileRef.current && fileRef.current.click()}>Upload files…</button>
              <button style={{ ...smallBtn, opacity: hasSet || !postedSheets ? 0.5 : 1 }} disabled={hasSet || !postedSheets} onClick={addDrawingSet}
                title={!postedSheets ? 'The project has no posted drawing sheets yet' : ''}>
                Add the current drawing set{prep ? ' (' + postedSheets + ' sheet' + (postedSheets === 1 ? '' : 's') + ')' : ''}
              </button>
              <span style={muted}>or drop files here</span>
              <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = '' }} />
            </div>

            {items.length === 0 && <div style={muted}>No files yet. Each file gets its own size, color, copies and binding.</div>}
            {items.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, ...label, marginBottom: 0 }}>{items.length} file{items.length === 1 ? '' : 's'}</div>
                  {items.length > 1 && <button style={{ ...smallBtn, padding: '4px 10px', fontSize: 11 }} onClick={sameForAll}>Use the first file's options for all</button>}
                </div>
                {items.map(it => (
                  <div key={it.key} style={{ border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: 'var(--aj-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.name}
                      </span>
                      <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted)' }}>
                        {it.uploading ? 'uploading…' : it.source === 'drawing_set' ? 'merged when sent' : fmtBytes(it.bytes)}
                      </span>
                      <button onClick={() => removeItem(it.key)} title="Remove" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 2 }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Select list={PRINT_SIZES} value={it.size} onChange={v => setItem(it.key, { size: v })} width={170} />
                      <Select list={PRINT_COLORS} value={it.color} onChange={v => setItem(it.key, { color: v })} width={150} />
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="number" min="1" max="999" value={it.copies} style={{ ...select, width: 64, fontFamily: MONO }}
                          onChange={e => setItem(it.key, { copies: e.target.value })} />
                        <span style={muted}>copies</span>
                      </span>
                      <Select list={PRINT_BINDINGS} value={it.binding} onChange={v => setItem(it.key, { binding: v })} width={180} />
                    </div>
                    <input style={{ ...input, fontSize: 12, padding: '6px 8px' }} value={it.note || ''} placeholder="Note for this file (optional), e.g. pages 1-4 only, mount on foam board"
                      onChange={e => setItem(it.key, { note: e.target.value })} />
                  </div>
                ))}
              </div>
            )}
          </>)}

          {step === 1 && (<>
            <div>
              <label style={label}>Send to</label>
              {contactIds.length === 0 && !generalEmail && (
                <div style={{ ...muted, color: '#b26a00' }}>{orgName} has no people or general email in Contacts yet. Add them there first.</div>
              )}
              {contactIds.map(cid => (
                <label key={cid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 4px', cursor: emails[cid] ? 'pointer' : 'default', opacity: emails[cid] || !(cid in emails) ? 1 : 0.5 }}>
                  <input type="checkbox" disabled={!emails[cid]} checked={!!checked[cid] && !!emails[cid]} onChange={() => setChecked(c => ({ ...c, [cid]: !c[cid] }))} />
                  <span style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text)' }}>{names[cid] || '…'}</span>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted)' }}>{emails[cid] || 'no email'}</span>
                </label>
              ))}
              {generalEmail && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 4px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={useGeneral} onChange={() => setUseGeneral(v => !v)} />
                  <span style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text)' }}>Shop's general email</span>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted)' }}>{generalEmail}</span>
                </label>
              )}
              <div style={{ ...muted, marginTop: 4 }}>Sent from {senderEmail || 'the firm'}, with replies to you and a copy to you.</div>
            </div>
            <div>
              <label style={label}>Before printing</label>
              <ChoiceCards options={PRINT_APPROVALS} value={approval} onPick={setApproval} />
            </div>
            <div>
              <label style={label}>Delivery</label>
              <ChoiceCards options={PRINT_DELIVERY.map(o => ({ ...o, d: o.v === 'office' && prep ? prep.office : null }))} value={deliveryMethod} onPick={setDeliveryMethod} />
              {(deliveryMethod === 'address' || deliveryMethod === 'ship') && (
                <textarea style={{ ...input, minHeight: 56, marginTop: 8, resize: 'vertical' }} value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)}
                  placeholder={deliveryMethod === 'ship' ? 'Ship to (name, address, and carrier or speed if it matters)' : 'Deliver to (name and address)'} />
              )}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ width: 200 }}>
                <label style={label}>Needed by (optional)</label>
                <input type="date" style={input} value={needBy} onChange={e => setNeedBy(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Note for the shop (optional)</label>
                <textarea style={{ ...input, minHeight: 56, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="e.g. paper, call when ready, who will pick it up" />
              </div>
            </div>
          </>)}

          {step === 2 && preview && (
            <div style={{ ...muted, fontSize: 12.5 }}>
              <div><b style={{ color: 'var(--aj-text-mid)', fontWeight: 500 }}>To:</b> {toList}</div>
              {senderEmail && <div><b style={{ color: 'var(--aj-text-mid)', fontWeight: 500 }}>Copy:</b> {senderEmail}</div>}
              <div><b style={{ color: 'var(--aj-text-mid)', fontWeight: 500 }}>Subject:</b> {preview.subject}</div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 10, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: 14, fontSize: 13, lineHeight: 1.5 }}>
                {preview.text}
              </div>
            </div>
          )}

          {error && <div style={{ fontFamily: FONT, fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1, ...muted }}>{step === 0 && uploading > 0 && 'Uploading ' + uploading + '…'}</div>
          {step === 0 && <>
            <button onClick={onClose} style={quietBtn}>Cancel</button>
            <button onClick={toOrder} disabled={!items.length || uploading > 0} style={primaryBtn(!items.length || uploading > 0)}>Next: order details</button>
          </>}
          {step === 1 && <>
            <button onClick={() => setStep(0)} disabled={busy} style={quietBtn}>Back</button>
            <button onClick={toPreview} disabled={busy} style={primaryBtn(busy)}>{busy ? 'Building preview…' : 'Preview email'}</button>
          </>}
          {step === 2 && <>
            <button onClick={() => setStep(1)} disabled={busy} style={quietBtn}>Back to edit</button>
            <button onClick={send} disabled={busy} style={primaryBtn(busy)}>{busy ? (hasSet ? 'Assembling drawings and sending…' : 'Sending…') : 'Send order to ' + orgName}</button>
          </>}
        </div>
      </div>
    </div>,
    document.body
  )
}
